/**
 * New Relic browser-agent error filtering.
 *
 * The agent is loaded from js-agent.newrelic.com, which ad/tracker blockers routinely block, so
 * `window.newrelic` may legitimately be undefined. Everything here must tolerate that: this code runs
 * at module-evaluation time in gatsby-browser.tsx, and a throw there aborts Gatsby's entire client
 * runtime (no hydration, no event handlers). See MW-13119.
 */

type NewRelicAgent = {
  setErrorHandler?: (handler: (error: Error) => boolean) => void;
};

export const isError = (value: unknown): value is Error => {
  if (!value) { return false; }

  // Check if it's an actual Error instance
  if (value instanceof Error) { return true; }

  // Check if it has error-like properties
  const maybeError = value as Partial<Error> & { toString?: () => string };
  return (
    typeof value === 'object'
    && (typeof maybeError.message === 'string'
      || typeof maybeError.name === 'string'
      || typeof maybeError.stack === 'string'
      // Handle cases where error is serialized
      || (maybeError.toString?.() ?? '').includes('Error:'))
  );
};

/**
 * Returning true tells New Relic to drop the error. We drop noise we cannot act on: errors thrown by
 * browser extensions and chunk-load failures caused by stale deploys or flaky networks.
 */
export const shouldIgnoreError = (error: Error): boolean => {
  if (
    isError(error) && (
      error.stack?.includes('chrome-extension:')
      || error.name?.includes('ChunkLoadError')
    )
  ) {
    return true;
  }
  return false;
};

/**
 * Register shouldIgnoreError with the New Relic agent if, and only if, the agent is present.
 *
 * @returns true when the handler was registered, false when the agent is unavailable.
 */
export const installNewRelicErrorHandler =
  (win: Window | undefined = globalThis.window): boolean => {
    const agent = (win as (Window & { newrelic?: NewRelicAgent }) | undefined)?.newrelic;
    if (typeof agent?.setErrorHandler !== 'function') {
      return false;
    }
    try {
      agent.setErrorHandler(shouldIgnoreError);
      return true;
    } catch {
      // Never let telemetry setup take the page down.
      return false;
    }
  };
